import { useState, useEffect } from 'react';
import { apiUrl } from '@app/constants';

// This is returned from the Engage /auth/niche API for user/storyteller info
export interface NicheUserData {
	id: string;
	email: string;
	workspaces: string[];
}

export interface NicheAuthResponse {
	user: NicheUserData;
	isNicheUser?: boolean;
	workspaceId?: string;
}

/**
 * Check for Niche authentication via the new GET endpoint
 * This endpoint reads the HttpOnly cookie server-side
 *
 * @param entityUUID - Optional entity UUID to get workspace-specific auth
 */
export const checkNicheAuthFromCookie = async (
	entityUUID?: string,
): Promise<NicheAuthResponse | null> => {
	try {
		const isLocal =
			window.location.hostname === 'localhost' ||
			window.location.hostname === '127.0.0.1';

		const endpoint = entityUUID
			? `${apiUrl}/auth/niche/${entityUUID}`
			: `${apiUrl}/auth/niche`;

		const response = await fetch(endpoint, {
			method: 'GET',
			credentials: isLocal ? 'same-origin' : 'include', // Use 'same-origin' for local, 'include' for production
			headers: {
				'Content-Type': 'application/json',
			},
		});

		if (!response.ok) {
			return null;
		}

		const data = await response.json();

		if (data.isNicheUser && data.user) {
			localStorage.setItem('nicheUser', JSON.stringify(data.user));

			// Store workspace ID if provided (from entity UUID)
			if (data.workspaceId) {
				localStorage.setItem('entityWorkspaceId', data.workspaceId);
			}

			return {
				user: data.user,
				isNicheUser: true,
				workspaceId: data.workspaceId,
			};
		}

		return null;
	} catch (error) {
		return null; // Always return null on error to allow normal flow
	}
};

export const performNicheAuthCheck = async (): Promise<boolean> => {
	try {
		const nicheAuthResult = await checkNicheAuthFromCookie();

		if (nicheAuthResult) {
			localStorage.setItem('checkAuthProvider', 'niche');
			return true;
		} else {
			return false;
		}
	} catch (error) {
		return false;
	}
};

/**
 * For already-authenticated users: check if entity UUID corresponds to a different workspace.
 * If yes, return the new workspace ID. If same or error, return null.
 *
 * @param entityUUID - Entity UUID from URL path
 * @param currentWorkspaceId - Current workspace ID
 * @returns New workspace ID if different, null otherwise
 */
export const getWorkspaceIdFromEntity = async (
	entityUUID: string,
	currentWorkspaceId: string,
): Promise<string | null> => {
	try {
		const response = await checkNicheAuthFromCookie(entityUUID);

		if (response?.workspaceId) {
			// If workspace ID is different from current, return it
			if (response.workspaceId !== currentWorkspaceId) {
				return response.workspaceId;
			}
		}

		return null; // Same workspace or no workspace found
	} catch (error) {
		console.error('Error fetching workspace from entity UUID:', error);
		return null;
	}
};

export const useNicheAuth = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [user, setUser] = useState<NicheUserData | null>(() => {
		const storedUser = localStorage.getItem('nicheUser');
		return storedUser ? JSON.parse(storedUser) : null;
	});
	const [error, setError] = useState<string | null>(null);
	const [isAuthenticated, setIsAuthenticated] = useState(() => {
		// Only consider authenticated if the current auth provider is niche
		const currentAuthProvider = localStorage.getItem('checkAuthProvider');
		return (
			currentAuthProvider === 'niche' && !!localStorage.getItem('nicheUser')
		);
	});
	const [accessToken, setAccessToken] = useState<string | null>(() => {
		return localStorage.getItem('accessToken');
	});
	const [hasCheckedNiche, setHasCheckedNiche] = useState(false);

	useEffect(() => {
		// Only run niche auth check if:
		// 1. We haven't checked niche auth yet
		// 2. User is not authenticated
		// 3. No auth provider is set OR auth provider is specifically set to niche
		const currentAuthProvider = localStorage.getItem('checkAuthProvider');
		const shouldCheckNiche =
			!hasCheckedNiche &&
			!isAuthenticated &&
			(!currentAuthProvider || currentAuthProvider === 'niche');

		if (shouldCheckNiche) {
			const checkNicheAuth = async () => {
				try {
					// Extract entityUUID from URL if present
					const pathname = window.location.pathname;
					const entityMatch = pathname.match(/\/entity\/([a-f0-9-]{24,36})/i);
					const entityUUID = entityMatch ? entityMatch[1] : undefined;

					const nicheAuthData = await checkNicheAuthFromCookie(entityUUID);

					if (nicheAuthData) {
						setUser(nicheAuthData.user);
						setIsAuthenticated(true);
						setAccessToken(null);

						// Set auth provider for the app
						localStorage.setItem('checkAuthProvider', 'niche');

						// Preserve redirect parameter when redirecting to callback
						const currentParams = new URLSearchParams(window.location.search);
						const redirectParam = currentParams.get('redirect');
						const callbackUrl = redirectParam
							? `/callback?niche=true&redirect=${encodeURIComponent(redirectParam)}`
							: '/callback?niche=true';

						window.location.href = callbackUrl;
					}
				} catch (error) {
				} finally {
					setHasCheckedNiche(true);
				}
			};

			checkNicheAuth();
		}
	}, [hasCheckedNiche, isAuthenticated]);

	// Listen for storage changes to detect auth provider switches
	useEffect(() => {
		const handleStorageChange = (e: StorageEvent) => {
			if (e.key === 'checkAuthProvider') {
				const newAuthProvider = e.newValue;
				if (newAuthProvider && newAuthProvider !== 'niche') {
					// Clear niche-specific state when switching to other providers
					setUser(null);
					setIsAuthenticated(false);
					setError(null);
					setAccessToken(null);
				}
			}
		};

		window.addEventListener('storage', handleStorageChange);
		return () => window.removeEventListener('storage', handleStorageChange);
	}, []);

	const authenticateNicheUser = async () => {
		setIsLoading(true);
		setError(null);

		try {
			// Use the same GET endpoint for manual authentication
			const response = await checkNicheAuthFromCookie();

			if (response && response.isNicheUser) {
				setUser(response.user);
				setIsAuthenticated(true);
				setAccessToken(null);
				localStorage.setItem('checkAuthProvider', 'niche');

				// Preserve redirect parameter when redirecting to callback
				const currentParams = new URLSearchParams(window.location.search);
				const redirectParam = currentParams.get('redirect');
				const callbackUrl = redirectParam
					? `/callback?niche=true&redirect=${encodeURIComponent(redirectParam)}`
					: '/callback?niche=true';

				window.location.href = callbackUrl;
			} else {
				setError('No Niche authentication found');
				setIsAuthenticated(false);
				localStorage.removeItem('checkAuthProvider');
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Authentication failed');
			setIsAuthenticated(false);
			// Clear the auth provider so the user can try other methods
			localStorage.removeItem('checkAuthProvider');
		} finally {
			setIsLoading(false);
		}
	};

	const logout = async ({ returnTo }: { returnTo: string }) => {
		try {
			// Call backend logout endpoint to clear httpOnly cookie
			const isLocal =
				window.location.hostname === 'localhost' ||
				window.location.hostname === '127.0.0.1';

			await fetch(`${apiUrl}/auth/niche/logout`, {
				method: 'POST',
				credentials: isLocal ? 'same-origin' : 'include',
				headers: {
					'Content-Type': 'application/json',
				},
			});
		} catch (error) {
			// Continue with client-side cleanup even if API call fails
		}

		// Clear client-side state and storage
		setUser(null);
		setIsAuthenticated(false);
		setError(null);
		localStorage.removeItem('accessToken');
		localStorage.removeItem('identity');
		localStorage.removeItem('nicheUser');
		localStorage.removeItem('checkAuthProvider');

		// Redirect to logout URL
		window.location.href = returnTo;
	};

	return {
		isLoading,
		user,
		error,
		isAuthenticated,
		logout,
		getAccessTokenSilently: () => Promise.resolve(accessToken),
		loginWithRedirect: authenticateNicheUser,
	};
};
